/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import useInventoryContext from "hooks/useInventoryContext"
import { useMemo } from "react"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { isSpecialItem } from "utils/item_helper"
import { EditableFieldsMap, editableFieldsMapping } from "./constant"

export const allFieldsAreEditableMap = Object.values(
  editableFieldsMapping,
).reduce((acc, curr) => {
  return { ...acc, [curr]: true }
}, {}) as EditableFieldsMap

function useEditableFields(): {
  editableFields: EditableFieldsMap
  isAllFieldsEditable?: boolean
  multiPics?: {
    enable: boolean
    maxSize: number
  }
} {
  const {
    specialItemSettings = [],
    item,
    multiPicsSetting,
    enableAlcoholSetting,
    isMenuGroup,
  } = useSelector((s: AppState) => ({
    specialItemSettings: s.menu.enableTroyNewMerchantEndpoint
      ? s.menu.outletV2?.merchantFlag?.specialItemSettings
      : s.menu.outlet?.merchantFlag?.specialItemSettings,
    item: s.menu.drawer.item,
    multiPicsSetting: s.menu.enableTroyNewMerchantEndpoint
      ? s.menu.outletV2?.merchantFlag?.multiPicsSetting
      : s.menu.outlet?.merchantFlag?.multiPicsSetting,
    enableAlcoholSetting: s.menu.enableAlcoholSetting,
    isMenuGroup: s.menu.isMenuGroup,
  }))

  const { mustEditThroughMenu } = useInventoryContext()
  const editableFieldsMap = useMemo(() => {
    if (mustEditThroughMenu) {
      // since mustEditThroughMenu has a higher precedence
      return {
        editableFields: {},
        multiPics: {
          enable: (multiPicsSetting?.enable && !isMenuGroup) ?? false,
          maxSize: multiPicsSetting?.maxSize ?? 0,
        },
      }
    }
    if (!item || !isSpecialItem(item)) {
      // all fields should be editable
      return {
        editableFields: {
          ...allFieldsAreEditableMap,
        },
        isAllFieldsEditable: true,
        multiPics: {
          enable: (multiPicsSetting?.enable && !isMenuGroup) ?? false,
          maxSize: multiPicsSetting?.maxSize ?? 0,
        },
      }
    }
    const { editableFields = [], enableMerchantMenuEditor = false } =
      specialItemSettings.find(e => e.specialItemType === "alcohol") ?? {}
    if (enableMerchantMenuEditor || enableAlcoholSetting) {
      return {
        editableFields: {
          ...allFieldsAreEditableMap,
        },
        isAllFieldsEditable: true,
        multiPics: {
          enable: (multiPicsSetting?.enable && !isMenuGroup) ?? false,
          maxSize: multiPicsSetting?.maxSize ?? 0,
        },
      }
    }
    const out: EditableFieldsMap = {}
    editableFields.forEach(field => {
      const fieldKey =
        editableFieldsMapping[
          field.toLowerCase() as keyof typeof editableFieldsMapping
        ]
      if (fieldKey) {
        out[fieldKey] = true
      }
    })
    return {
      editableFields: out,
      isAllFieldsEditable:
        Object.keys(out).length === Object.keys(allFieldsAreEditableMap).length,
      multiPics: {
        enable: (multiPicsSetting?.enable && !isMenuGroup) ?? false,
        maxSize: multiPicsSetting?.maxSize ?? 0,
      },
    }
  }, [
    mustEditThroughMenu,
    item,
    specialItemSettings,
    multiPicsSetting,
    enableAlcoholSetting,
    isMenuGroup,
  ])
  return editableFieldsMap
}

export default useEditableFields
